import { useTheme } from '@repo/theme';
import { createThemedStyles } from '@repo/theme/styles';
import { HorizBox, Pad } from 'component/basics';
import { UtilityText } from 'component/text';
import type { ComponentType } from 'react';
import { useEffect, useRef, useState } from 'react';
import type { GestureResponderEvent, LayoutChangeEvent } from 'react-native';
import { Animated, PanResponder, Pressable } from 'react-native';
import { View } from 'react-native-web';

/** @public */
export const EstimationSliderFeature = {
    name: 'Estimation Slider',
    key: 'estimationslider',
    providesConfig: {},
};

export interface EstimationSliderProps {
    min: number;
    max: number;
    stepSize?: number;
    value?: number;
    onChangeValue: (value: number) => void;
    unit?: string;
    isUnitBeforeValue?: boolean;
    snapOnMove?: boolean;
    AnimationComponent?: ComponentType;
}

// Problem is the coupled xPosition to the image animation, maybe a sperate
// wrapper component can enable seperation FIXME: This function is too large and
// should be broken up
/* eslint-disable-next-line max-lines-per-function */
export function EstimationSlider({
    min,
    max,
    stepSize = 1,
    value = min,
    onChangeValue,
    unit = '',
    isUnitBeforeValue = false,
    snapOnMove = false,
    AnimationComponent = undefined,
}: EstimationSliderProps) {
    const { colors } = useTheme();
    const s = useDiscreteSliderStyles();

    const [isDragging, setIsDragging] = useState(false);
    const directPress = useRef(false);

    const [componentWidth, setComponentWidth] = useState<number | null>(null);
    const xPosition = useRef(new Animated.Value(0)).current;
    const [hasLayout, setHasLayout] = useState(false);
    const barRef = useRef<View>(null);

    const [hoverValue, setHoverValue] = useState(value !== undefined ? value : min);
    const numberOfStops = (max - min) / stepSize + 1; // +1 to include max

    const [showAnimation, setShowAnimation] = useState(false);
    // unique key to make it possible to rerender animations without changing
    // props
    const [animationKey, setAnimationKey] = useState(0);

    const onChangeRef = useRef(onChangeValue);
    onChangeRef.current = onChangeValue;

    function getSpaceBetweenStops(): number | undefined {
        if (!barRef.current) return;
        // FIXME WEB-ONLY: getBoundingClientRect is not supported in React
        // Native, use measure() instead
        const rect = (barRef.current as any).getBoundingClientRect();
        return rect.width / (numberOfStops - 1);
    }

    /**Calculates the nearest "stop" for a value */
    function valueToXPosition(value: number): number | undefined {
        const stopWidth = getSpaceBetweenStops();
        if (!stopWidth) return;
        return Math.round((value - min) / stepSize) * stopWidth;
    }

    /**
     * Calculates the distance of the current position to the left side of the
     * bar
     */
    function screenXPositionXOffset(xPosition: number): number {
        // FIXME: check for null FIXME WEB-ONLY: getBoundingClientRect is not
        // supported in React Native, use measure() instead
        const rect = (barRef.current! as any).getBoundingClientRect();
        return xPosition - rect.left;
    }

    /**Converts x Position to the clostes stop value*/
    function xPositionToValue(xPosition: number): number | undefined {
        const stopWidth = getSpaceBetweenStops();
        if (!stopWidth) return;
        const roundedStops = Math.round(xPosition / stopWidth);
        return Math.max(0, Math.min(max - min, roundedStops * stepSize)) + min;
    }

    useEffect(() => {
        setHoverValue(value);
    }, [value]);

    const panResponder = useRef(
        PanResponder.create({
            onStartShouldSetPanResponder: () => true,
            onMoveShouldSetPanResponder: () => true,
            onPanResponderTerminate: () => {
                setIsDragging(false);
            },
            onPanResponderGrant: () => {
                xPosition.flattenOffset();
            },
            onPanResponderMove: (_, gestureState) => {
                setIsDragging(true);
                setShowAnimation(false);
                const dragX = screenXPositionXOffset(gestureState.moveX);
                // FIXME: check for null FIXME WEB-ONLY: getBoundingClientRect
                // is not supported in React Native, use measure() instead
                const rect = (barRef.current! as any).getBoundingClientRect();
                const clippedX = Math.max(0, Math.min(rect.width, dragX));
                const newHoverValue = xPositionToValue(clippedX);
                if (newHoverValue !== undefined) {
                    snapOnMove ? xPosition.setValue(valueToXPosition(newHoverValue) ?? 0) : xPosition.setValue(clippedX);
                    setHoverValue(newHoverValue);
                }
            },
            onPanResponderRelease: (_, gestureState) => {
                setIsDragging(false);
                setShowAnimation(true);

                // Prevents the handle from snapping to the start of the slider
                // when you click the same spot twice
                if (gestureState.moveX === 0 && gestureState.moveY === 0) {
                    return;
                }

                const pressX = screenXPositionXOffset(gestureState.moveX);
                // FIXME: check for null FIXME WEB-ONLY: getBoundingClientRect
                // is not supported in React Native, use measure() instead
                const rect = (barRef.current! as any).getBoundingClientRect();
                const clippedX = Math.max(0, Math.min(rect.width, pressX));
                const newValue = xPositionToValue(clippedX);
                if (newValue === undefined) return;
                const newXPosition = valueToXPosition(newValue);
                xPosition.setValue(clippedX);
                if (!newXPosition) return;
                Animated.parallel([Animated.timing(xPosition, { toValue: newXPosition, duration: 1, useNativeDriver: false })], {
                    stopTogether: false,
                }).start(() => {});
                onChangeRef.current?.(newValue);
            },
        }),
    ).current;

    const handleBarPress = (e: GestureResponderEvent) => {
        if ((componentWidth ?? 0) > 0 && barRef.current) {
            const pressX = screenXPositionXOffset(e.nativeEvent.pageX);
            const newValue = xPositionToValue(pressX);
            if (newValue === undefined) return;
            const newXPosition = valueToXPosition(newValue);
            xPosition.setValue(newXPosition ?? 0);
            setHoverValue(newValue);
            setShowAnimation(true);
            setAnimationKey(animationKey + 1);
            if (directPress.current) {
                directPress.current = false;
                onChangeRef.current?.(newValue);
            }
        }
    };

    /**Adjusts the current x position value based on the current width */
    const handleLayout = (event: LayoutChangeEvent) => {
        const { width } = event.nativeEvent.layout;
        setComponentWidth(width);
        xPosition.setValue(valueToXPosition(hoverValue) ?? 0);
        setHasLayout(true);
    };

    return (
        // FIXME WEB-ONLY: userSelect is not supported in React Native
        <View style={{ userSelect: isDragging ? 'none' : 'auto' } as any}>
            <View>
                <Pad size={12} />
                <View style={s.overlapBar}>
                    <HoverHelpBar isUnitBeforeValue={isUnitBeforeValue} unit={unit} value={hoverValue.toString()} xPosition={xPosition} />
                </View>
            </View>
            <Pressable
                onPress={handleBarPress}
                onPressIn={() => {
                    directPress.current = true;
                }}
                ref={barRef}
                testID='slider-bar'
            >
                <View onLayout={handleLayout} style={s.container}>
                    {hasLayout && (
                        <Animated.View style={[s.coloredContainer, { width: (xPosition as any)._value < 0 ? 0 : xPosition }]}>
                            {hasLayout && (
                                <Animated.View
                                    {...panResponder.panHandlers}
                                    style={[s.dragHandle, { left: xPosition }, s.dragHandleShadow]}
                                    testID='slider-handle'
                                >
                                    <View style={s.animationContainer}>
                                        {AnimationComponent && showAnimation && <AnimationComponent key={animationKey} />}
                                    </View>
                                </Animated.View>
                            )}
                        </Animated.View>
                    )}
                </View>
            </Pressable>
            <HorizBox spread>
                {isUnitBeforeValue ? (
                    <>
                        <UtilityText color={colors.colorTextPrimary} text={unit ? unit + ' ' + min.toString() : min.toString()} />
                        <UtilityText color={colors.colorTextPrimary} text={unit ? unit + ' ' + max.toString() : max.toString()} />
                    </>
                ) : (
                    <>
                        <UtilityText color={colors.colorTextPrimary} text={min.toString() + (unit ? ' ' + unit : '')} />
                        <UtilityText color={colors.colorTextPrimary} text={max.toString() + (unit ? ' ' + unit : '')} />
                    </>
                )}
            </HorizBox>
        </View>
    );
}

const useDiscreteSliderStyles = createThemedStyles((tokens) => ({
    container: {
        width: '100%',
        height: 15,
        borderRadius: 4,
        marginVertical: 16,
        backgroundColor: tokens.colors.colorBorderPrimary,
        justifyContent: 'center',
    },
    image: {
        width: 125,
        height: 125,
        position: 'absolute',
        zIndex: -1,
        transformOrigin: 'bottom left',
    },
    animationContainer: {
        pointerEvents: 'none',
    },
    coloredContainer: {
        width: '0%',
        height: 15,
        borderRadius: 4,
        backgroundColor: tokens.colors.colorTextSecondary,
        justifyContent: 'center',
        zIndex: -1,
        position: 'relative',
    },
    overlapBar: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: 8,
    },
    dragHandle: {
        width: 25,
        height: 25,
        borderRadius: 16,
        backgroundColor: tokens.colors.colorDataVizScale2,
        position: 'absolute',
        borderColor: tokens.colors.colorBorderPrimary,
        cursor: 'pointer',
        zIndex: 10,
        alignItems: 'center',
        justifyContent: 'center',
        transform: 'translateX(-50%)',
        borderWidth: 2,
    },
    dragHandleShadow: {
        boxShadow: '0px 2px 10px rgba(0, 0, 0, 0.30)',
    },
    selectionStopPoint: {
        width: 6,
        height: 6,
        borderRadius: 0,
    },
}));

interface HoverHelpBarProps {
    value: string;
    xPosition: Animated.Value;
    unit?: string;
    isUnitBeforeValue?: boolean;
}

function HoverHelpBar({ value, xPosition, unit = '', isUnitBeforeValue = false }: HoverHelpBarProps) {
    const { colors } = useTheme();
    const s = useHoverHelpBarStyles();
    return (
        <View style={s.outer}>
            <Animated.View style={[s.hoverPill, { left: xPosition }]}>
                <UtilityText
                    color={colors.colorTextPrimary}
                    testID='slider-value'
                    text={isUnitBeforeValue ? (unit ? unit + ' ' + value : value) : unit ? value + ' ' + unit : value}
                    type='small'
                    weight='strong'
                />
            </Animated.View>
        </View>
    );
}

const useHoverHelpBarStyles = createThemedStyles((tokens) => ({
    label: {
        color: tokens.colors.colorPrimaryBrandWhite,
    },
    outer: {
        position: 'absolute',
        left: 0,
        right: 0,
        top: 0,
        bottom: 0,
    },
    hoverPill: {
        backgroundColor: tokens.colors.colorSurfacePrimary,
        flexDirection: 'row',
        alignItems: 'center',
        transform: [{ translateX: '-50%' }],
        flexGrow: 0,
        flexShrink: 1,
        alignSelf: 'baseline',
    },
}));
